import type { BaseEntity } from "@/components/shared/UniversalCRMTable/types";
import type {
  CustomFilterHook,
  CustomFilterHookParams,
  SimpleTableConfig,
} from "@/components/shared/UniversalCRMTable/types/shared";

/**
 * Always-called hook that calls all customFilterHooks in config, returns active result
 * This ensures stable hook order while only returning data for the requested view
 *
 * CRITICAL: All hooks in customFilterHooks MUST be called unconditionally to maintain
 * stable hook ordering. This hook calls all hooks that exist in the config.
 *
 * NOTE: React's Rules of Hooks require hooks to be called in the same order every render.
 * Since we iterate over config.customFilterHooks, the config MUST be stable (same keys
 * in same order) between renders. Configs are typically constants, so this is usually fine.
 */
export function useActiveCustomFilter<T extends BaseEntity>(
  config: SimpleTableConfig<T>,
  activeViewId: string | null,
  queryParams: { first: number; after: string | null }
) {
  const customFilterHooks = config.customFilterHooks || {};

  // If no customFilterHooks exist, return null early (no hooks to call)
  // This is safe because we're not calling any hooks here
  if (!customFilterHooks || Object.keys(customFilterHooks).length === 0) {
    return null;
  }

  // CRITICAL: Get sorted keys to ensure stable ordering
  // We MUST call hooks in the same order every render
  const hookKeys = Object.keys(customFilterHooks).sort();

  // CRITICAL: Call ALL hooks unconditionally in sorted order
  // We use explicit array indexing to call hooks in stable order
  // This ensures React sees the same number of hooks in the same order on every render
  const results: Record<string, ReturnType<CustomFilterHook<T>>> = {};

  // Call each hook by index to ensure stable order (can't use forEach due to Rules of Hooks)
  // This approach ensures hooks are called at the top level, not in a callback
  for (let i = 0; i < hookKeys.length; i++) {
    const key = hookKeys[i];
    const hook = customFilterHooks[key];
    if (hook && typeof hook === "function") {
      const hookParams: CustomFilterHookParams = {
        first: queryParams.first,
        after: queryParams.after,
        enabled: activeViewId === key,
      };
      // Call the hook unconditionally - this is a React hook call at the top level
      results[key] = hook(hookParams);
    }
  }

  // Return result for activeViewId, or null if no match
  // But ALL hooks were called above, so hook order is stable
  return activeViewId && results[activeViewId] ? results[activeViewId] : null;
}
